import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { OPERATIONS_DEFAULTS, operationsSchema, type OperationsState } from "./operationsSchema";

const ARRAY_KEYS = new Set(["requestType", "client", "lob", "eng", "assigned", "team", "division", "manager", "country", "dfAttention", "dfPending", "dfWorkType", "dfStartRisk", "dfOwner", "dfTeam", "dfDivision", "dfManager", "dfEngagement", "dfHolder"]);
const NUMBER_KEYS = new Set(["dAgeBin"]);
const BOOLEAN_KEYS = new Set(["onboardingsOnly", "redeployment"]);

function parseFromSearchParams(params: URLSearchParams): OperationsState {
  const raw: Record<string, unknown> = {};
  for (const key of ARRAY_KEYS) {
    const values = params.getAll(key);
    if (values.length > 0) raw[key] = values;
  }
  for (const [key, value] of params.entries()) {
    if (ARRAY_KEYS.has(key)) continue;
    if (NUMBER_KEYS.has(key)) {
      const n = Number(value);
      if (!Number.isNaN(n)) raw[key] = n;
    } else if (BOOLEAN_KEYS.has(key)) {
      raw[key] = value === "true";
    } else {
      raw[key] = value;
    }
  }
  const result = operationsSchema.safeParse(raw);
  if (result.success) return result.data;
  if (typeof console !== "undefined") {
    console.warn("[operations] invalid URL state:", result.error.issues);
  }
  const bad = new Set(result.error.issues.map((i) => i.path[0] as string));
  for (const k of bad) delete raw[k];
  return operationsSchema.parse(raw);
}

function serializeToSearchParams(state: OperationsState): URLSearchParams {
  const params = new URLSearchParams();
  const def = OPERATIONS_DEFAULTS as Record<string, unknown>;
  for (const [key, value] of Object.entries(state) as [keyof OperationsState, unknown][]) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length === 0) continue;
      for (const v of value) params.append(key, String(v));
      continue;
    }
    if (typeof value === "boolean") {
      if (value === def[key]) continue;
      params.set(key, value ? "true" : "false");
      continue;
    }
    if (def[key] === value) continue;
    params.set(key, String(value));
  }
  return params;
}

export function useOperationsUrlState(): [OperationsState, (next: Partial<OperationsState>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const state = useMemo(() => parseFromSearchParams(searchParams), [searchParams]);
  const set = useCallback(
    (next: Partial<OperationsState>) => {
      const merged = operationsSchema.parse({ ...state, ...next });
      setSearchParams(serializeToSearchParams(merged));
    },
    [state, setSearchParams],
  );
  return [state, set];
}
